import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, catchError, shareReplay, switchMap, tap } from 'rxjs';
import { common_urls, common_urls_cws2 } from 'src/environments/environment';
import { CwsRedirectService } from 'src/app/service/cws-redirect.service';
import { LoaderService } from 'src/app/service/loader.service';
import {
  BANK_ACCOUNT_CHANGE_API_URLS,
  BANK_ACCOUNT_CHANGE_PAPER_POLICY_PA_CODES,
  PolicyType,
} from '../bank-account-change.constants';
import {
  BankAccountChangeDigitalNttCallbackRequest,
  BankAccountChangeDigitalNttCallbackResponse,
  BankAccountChangeDigitalNttInitRequest,
  BankAccountChangeDigitalNttInitResponse,
  BankAccountChangeLoadRequest,
  BankAccountChangeLoadResponse,
  BankAccountChangePaperSubmitRequest,
  BankAccountChangePaperSubmitResponse,
  BankAccountChangePolicyType,
  BankAccountChangeSelectedPolicy,
  BankAccountChangeShippingAddress,
  BankAccountKanjiNameResponse,
  BankAccountChangeState,
  BankAccountSubmissionDetails,
} from './bank-account-change.interfaces';


@Injectable({
  providedIn: 'root',
})
export class BankAccountChangeService {
  private static readonly STORAGE_KEY = 'bank-account-change:state';
  private httpClient = inject(HttpClient);
  private oidcSecurityService = inject(OidcSecurityService);
  private cwsRedirectService = inject(CwsRedirectService);
  private loaderService = inject(LoaderService);
  private state: BankAccountChangeState = {};
  private kanjiName$?: Observable<BankAccountKanjiNameResponse>;
  private loadResponse$?: Observable<BankAccountChangeLoadResponse>;

  readonly cwsHomeUrl = `${common_urls.cwsBaseUrl}/home?From=auth&cws=1`;

  isProceedConfirmed = false;

  goToCwsHome(): void {
    window.location.href = this.cwsHomeUrl;
  }

  loadBankAccountChange(): Observable<BankAccountChangeLoadResponse> {
    if (!this.loadResponse$) {
      const payload: BankAccountChangeLoadRequest = {};
      this.loaderService.show();
      const request$ = this.oidcSecurityService.getAccessToken().pipe(
        switchMap((accessToken) =>
          this.httpClient.post<BankAccountChangeLoadResponse>(
            BANK_ACCOUNT_CHANGE_API_URLS.LOAD_BANK_ACCOUNT_CHANGE,
            payload,
            this.getRequestOptions(accessToken)
          )
        )
      );

      this.loadResponse$ = request$.pipe(
        tap((response) => {
          this.loaderService.hide();
          this.setShippingAddress({
            postalCode: response?.shippingPostalCode,
            address: response?.shippingAddress,
          });
        }),
        shareReplay(1),
        catchError((error) => {
          this.loaderService.hide();
          this.loadResponse$ = undefined;
          this.cwsRedirectService.redirectToCWSErrorPage();
          throw error;
        })
      );
    }

    return this.loadResponse$;
  }

  setShippingAddress(shippingAddress: BankAccountChangeShippingAddress): void {
    this.syncStateFromStorage();
    this.state.shippingAddress = { ...shippingAddress };
    this.persistState();
  }

  getShippingAddress(): BankAccountChangeShippingAddress | undefined {
    this.syncStateFromStorage();
    return this.state.shippingAddress ? { ...this.state.shippingAddress } : undefined;
  }

  submitBankAccountChangePaper(selectedPolicyNumbers: string[]): Observable<BankAccountChangePaperSubmitResponse> {
    const payload: BankAccountChangePaperSubmitRequest = { selectedPolicyNumbers };
    this.loaderService.show();
    return this.oidcSecurityService.getAccessToken().pipe(
      switchMap((accessToken) =>
        this.httpClient.post<BankAccountChangePaperSubmitResponse>(
          BANK_ACCOUNT_CHANGE_API_URLS.SUBMIT_BANK_ACCOUNT_CHANGE_PAPER,
          payload,
          this.getRequestOptions(accessToken)
        )
      ),
      tap((response) => {
        this.loaderService.hide();
      }),
      catchError((error) => {
        this.loaderService.hide();
        this.cwsRedirectService.redirectToCWSErrorPage();
        throw error;
      })
    );
  }

  initDigitalNttTransfer(selectedPolicyNumbers: string[], nayoseFlag: boolean): Observable<BankAccountChangeDigitalNttInitResponse> {
    const payload: BankAccountChangeDigitalNttInitRequest = { selectedPolicyNumbers, nayoseFlag };
    this.loaderService.show();
    return this.oidcSecurityService.getAccessToken().pipe(
      switchMap((accessToken) =>
        this.httpClient.post<BankAccountChangeDigitalNttInitResponse>(
          BANK_ACCOUNT_CHANGE_API_URLS.LOAD_BANK_ACCOUNT_CHANGE_DIGITAL_NTT_INIT,
          payload,
          this.getRequestOptions(accessToken)
        )
      ),
      tap((response) => {
        this.loaderService.hide();
      }),
      catchError((error) => {
        this.loaderService.hide();
        this.cwsRedirectService.redirectToCWSErrorPage();
        throw error;
      })
    );
  }

  processDigitalNttCallback(payload: BankAccountChangeDigitalNttCallbackRequest): Observable<BankAccountChangeDigitalNttCallbackResponse> {
    this.loaderService.show();
    return this.oidcSecurityService.getAccessToken().pipe(
      switchMap((accessToken) =>
        this.httpClient.post<BankAccountChangeDigitalNttCallbackResponse>(
          BANK_ACCOUNT_CHANGE_API_URLS.SUBMIT_BANK_ACCOUNT_CHANGE_DIGITAL,
          payload,
          this.getRequestOptions(accessToken)
        )
      ),
      tap((response) => {
        this.loaderService.hide();
      }),
      catchError((error) => {
        this.loaderService.hide();
        this.cwsRedirectService.redirectToCWSErrorPage();
        throw error;
      })
    );
  }

  getKanjiName(): Observable<BankAccountKanjiNameResponse> {
    if (!this.kanjiName$) {
      const apiEndpoint = `${common_urls_cws2.bffBaseUrl_cws2}/user-management/userdetails/getkanjiname`;
      this.kanjiName$ = this.oidcSecurityService
        .getAccessToken()
        .pipe(
          switchMap((accessToken) =>
            this.httpClient.get<BankAccountKanjiNameResponse>(apiEndpoint, this.getRequestOptions(accessToken))
          ),
          shareReplay(1),
          catchError((error) => {
            this.kanjiName$ = undefined;
            this.cwsRedirectService.redirectToCWSErrorPage();
            throw error;
          })
        );
    }

    return this.kanjiName$;
  }

  setPolicyholderName(policyholderName: string): void {
    this.state.policyholderName = policyholderName;
    this.persistState();
  }

  getPolicyholderName(): string {
    this.syncStateFromStorage();
    return this.state.policyholderName ?? '';
  }

  setSelectedPolicyType(selectedPolicyType: BankAccountChangePolicyType): void {
    this.state.selectedPolicyType = selectedPolicyType;
    this.persistState();
  }

  clearSelectedPolicyType(): void {
    delete this.state.selectedPolicyType;
    this.persistState();
  }

  getSelectedPolicyType(): BankAccountChangePolicyType | undefined {
    this.syncStateFromStorage();
    return this.normalizePolicyType(this.state.selectedPolicyType);
  }

  setSelectedPolicies(policies: BankAccountChangeSelectedPolicy[]): void {
    this.state.selectedPolicies = [...policies];
    this.persistState();
  }

  getSelectedPolicies(): BankAccountChangeSelectedPolicy[] {
    this.syncStateFromStorage();
    return [...(this.state.selectedPolicies ?? [])];
  }

  setSubmissionDetails(details: BankAccountSubmissionDetails): void {
    this.state.submissionDetails = { ...details };
    this.persistState();
  }

  getSubmissionDetails(): BankAccountSubmissionDetails | undefined {
    this.syncStateFromStorage();
    return this.state.submissionDetails ? { ...this.state.submissionDetails } : undefined;
  }

  derivePolicyType(policy: Pick<BankAccountChangeSelectedPolicy, 'paCode'>): PolicyType {
    return policy.paCode && BANK_ACCOUNT_CHANGE_PAPER_POLICY_PA_CODES.includes(policy.paCode as typeof BANK_ACCOUNT_CHANGE_PAPER_POLICY_PA_CODES[number])
      ? PolicyType.PAPER
      : PolicyType.DIGITAL;
  }

  clearState(): void {
    this.state = {};
    this.isProceedConfirmed = false;
    this.loadResponse$ = undefined;
    this.removeStateFromStorage();
  }

  private persistState(): void {
    try {
      sessionStorage.setItem(BankAccountChangeService.STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // Ignore storage write errors and keep in-memory fallback.
    }
  }

  private syncStateFromStorage(): void {
    if (Object.keys(this.state).length > 0) {
      return;
    }

    try {
      const raw = sessionStorage.getItem(BankAccountChangeService.STORAGE_KEY);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw) as BankAccountChangeState;
      this.state = parsed ? {
        ...parsed,
        selectedPolicyType: this.normalizePolicyType(parsed.selectedPolicyType),
      } : {};
    } catch {
      this.state = {};
    }
  }

  private removeStateFromStorage(): void {
    try {
      sessionStorage.removeItem(BankAccountChangeService.STORAGE_KEY);
    } catch {
      // Ignore storage removal errors.
    }
  }

  private getRequestOptions(accessToken: string): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
    };
  }

  private normalizePolicyType(value: BankAccountChangePolicyType | string | undefined): PolicyType | undefined {
    if (!value) {
      return undefined;
    }

    if (value === PolicyType.DIGITAL || value === 'digital') {
      return PolicyType.DIGITAL;
    }

    if (value === PolicyType.PAPER || value === 'paper') {
      return PolicyType.PAPER;
    }

    return undefined;
  }
}
